"use client";

import { useCallback } from "react";

import { useRegisterClientVerification } from "#app/(unauthorized)/register/(client)/_shared/register-client-hooks";
import { RegisterClientStep0 } from "#app/(unauthorized)/register/(client)/_shared/register-client-step-0";
import { RegisterClientStep1 } from "#app/(unauthorized)/register/(client)/_shared/register-client-step-1";
import {
  steps,
  stepsStateMapping,
} from "#app/(unauthorized)/register/(client)/_shared/register-client-utils";
import { useRegistrationErrors } from "#app/(unauthorized)/register/_shared/register-hooks";
import { ErrorCard } from "#app/_ui/components/cards/ErrorCard";
import { PageProgress } from "#app/_ui/components/cards/PageProgress";
import { PendingCard } from "#app/_ui/components/cards/PendingCard";
import { P } from "#ui/typography";

/**
 * This page handles the first 2 steps of the client registration process.
 * The final steps are handled in the `/register/create` page.
 */
export function RegisterClient() {
  // For outages and handling registration service errors
  const { error, isError, isPending, isSuccess, setDisabledMessage } =
    useRegistrationErrors();

  // For managing verification steps (0-1), form values, and async errors
  const {
    asyncError,
    currentStep,
    email,
    handleStep0Submit,
    isPendingMutation,
  } = useRegisterClientVerification();

  // Memoized callback for Step 0
  const handleStep0SubmitSuccess = useCallback(
    (formValues: Parameters<typeof handleStep0Submit>[0]["formValues"]) =>
      handleStep0Submit({ formValues, setDisabledMessage }),
    [handleStep0Submit, setDisabledMessage],
  );

  return (
    <PageProgress current={currentStep} steps={steps}>
      {isPending ? <PendingCard /> : null}

      {isError ? (
        <ErrorCard>
          <P size="sm" palette="neutral-subtle">
            {error}
          </P>
        </ErrorCard>
      ) : null}

      {isSuccess &&
      currentStep === stepsStateMapping.step0PersonalInformation ? (
        <RegisterClientStep0
          asyncError={asyncError}
          isPending={isPendingMutation}
          onSubmitSuccess={handleStep0SubmitSuccess}
        />
      ) : null}

      {isSuccess && currentStep === stepsStateMapping.step1EmailVerification ? (
        <RegisterClientStep1 email={email} />
      ) : null}

      {/*
        Uncovered state: PageProgress w/ no children.
        It shouldn't be possible to reach this state,
        as all steps are handled above.
      */}
    </PageProgress>
  );
}
